import {
    CBOSessionStatus,
    ICBOCalculator,
    ICBOCalculatorCalculateOptions,
    ICBOCalculatorDetailProgress,
} from "../PA.Simulation.Services/CBOServices.IF";
import { MessageProvider } from "../PA.Core/Basic/Messages";
import { ProgressProvider } from "../PA.Core/Basic/Progress";
import { ConstraintBasedOptimizerBO } from "../PA.Simulation.BusinessObjects/ConstraintBasedOptimizerBO";
import { BehaviorSubject } from "rxjs";
import * as _ from "lodash";
import { ASync } from "../PA.Core/Basic/ASync";
import { IMessageProvider, IProgressProvider, MessageType } from "../PA.Core/PA.Core.IF.Basic";
import { CBOService } from "./CBOService";
import { DateUtils } from "../PA.Core/Basic/Dates";
import { esWB_CBO } from "../PA.Core/PAdbSchema";

const msStatusLoopPollingSlow = 5000;
const msStatusLoopPollingFast = 2000;

export class CBOCalculator implements ICBOCalculator {
    private _calcMessageProvider: MessageProvider;
    private _calcProgressProvider: ProgressProvider;
    private _onCalculationComplete: BehaviorSubject<CBOCalculator>;
    private _CBOBO: ConstraintBasedOptimizerBO;
    private _CBOService: CBOService;

    private _detailStatus: ICBOCalculatorDetailProgress;
    private _onDetailedProgressChanged: BehaviorSubject<CBOCalculator>;

    private _iStatusReceivedCount: number;
    private _bStatusTimerId: NodeJS.Timeout;

    //   private _CalculateCompleteSubscription: Subscription
    constructor() {
        this._calcMessageProvider = new MessageProvider();
        this._calcProgressProvider = new ProgressProvider();
        this._onCalculationComplete = new BehaviorSubject<CBOCalculator>(this);
        this._CBOBO = undefined;
        this._CBOService = new CBOService();
        this._iStatusReceivedCount = 0;
        this._bStatusTimerId = undefined;
        this._detailStatus = {
            bRunning: false,
            CalculationStep: undefined,
            TotalNumberOfSimulations: 0,
            NumberOfSimulationsCompleted: 0,
            ElapsedTimeInSeconds: 0,
            EstimatedRemainingTimeInSeconds: 0,
        };
        this._onDetailedProgressChanged = new BehaviorSubject<CBOCalculator>(this);

       // this.RunStatusLoop();
    }

    Register(CBOBO: ConstraintBasedOptimizerBO): void {
        const self = this;
        const bo = CBOBO;
        // Currently, we don't listen to anything,.. just on demand calc.

        this.UnRegister();
        this._CBOBO = CBOBO;

        if (bo) {
            bo.OnLoadComplete.then((result) => {
                return ASync.resolve();
            })
                .then((ignore) => {})
                .catch((err) => {
                    console.log("Error in CBO Evaluator Register(): ", err);
                });
        }
    }

    UnRegister(): void {
        this._CBOBO = undefined;
    }

    private RunStatusLoop() {
        const self = this;

        let nextIterationDelay = msStatusLoopPollingSlow;
        self.UpdateStatus()
            .then((bGoFast) => {
                if (bGoFast) nextIterationDelay = msStatusLoopPollingFast;

                self._bStatusTimerId = setTimeout(self.RunStatusLoop.bind(self), nextIterationDelay);
            })
            .catch((err) => {
                self._bStatusTimerId = setTimeout(self.RunStatusLoop.bind(self), nextIterationDelay);
            });
    }

    StopStatusLoop() {
        const self = this;

        if (self._bStatusTimerId) {
            clearTimeout(self._bStatusTimerId);
            self._bStatusTimerId = undefined;
        }
    }

    private async UpdateStatus(): Promise<boolean> {
        const self = this;

        let bGoFast = false;

        try {
            const wbCBOid = self._CBOBO?.WB_CBO?.cif?.WB_CBO_ID || 0;

            if (wbCBOid > 0) {
                const previousDetailStatus = _.clone(self._detailStatus);
                const response = await self._CBOService.GetCBOStatus(wbCBOid, {});

                self._detailStatus.CalculationStep = <any>response?.Results || "";
                self._detailStatus.TotalNumberOfSimulations = response?.TotalNumberOfSimulations || 0;
                self._detailStatus.NumberOfSimulationsCompleted = response?.NumberOfSimulationsCompleted || 0;
                self._detailStatus.ElapsedTimeInSeconds = response?.ElapsedTimeInSeconds || 0;
                self._detailStatus.EstimatedRemainingTimeInSeconds = response?.EstimatedRemainingTimeInSeconds || 0;

                switch (true) {
                    case response?.Results == CBOSessionStatus.Done:
                    case response?.Results == CBOSessionStatus.NotStarted:
                    case _.isEmpty(response?.Results):
                        // Do nothing, Go Slow
                        self._detailStatus.bRunning = false;
                        break;
                    default:
                        self._detailStatus.bRunning = true;
                        bGoFast = true;
                }

                if (!_.isEqual(previousDetailStatus, self._detailStatus)) {
                    // Change in Status
                    self.OnDetailedProgressChanged.next(self);
                }

                if (
                    previousDetailStatus.CalculationStep &&
                    self._detailStatus.CalculationStep &&
                    previousDetailStatus.CalculationStep != self._detailStatus.CalculationStep
                ) {
                    switch (self._detailStatus.CalculationStep) {
                        case CBOSessionStatus.NotStarted:
                        case CBOSessionStatus.Done:
                            self._CBOBO.ReloadMetrics().then(() => {
                                self.OnCalculateComplete.next(self);
                            });
                    }
                }

                // Update Progress Provider
                switch (self._detailStatus.CalculationStep) {
                    case CBOSessionStatus.NotStarted:
                        if (self.CalcProgressProvider.process_running) self.CalcProgressProvider.finish();
                        break;
                    case CBOSessionStatus.CreatingSimulations:
                        self.CalcProgressProvider.setProgressMessage("1", "Creating Simulations");
                        break;

                    case CBOSessionStatus.DoneCreatingSimulations:
                        self.CalcProgressProvider.setProgressMessage("2", "Created Simulations");
                        break;
                    case CBOSessionStatus.CalculatingSimulations:
                        self.CalcProgressProvider.setProgressMessage("3", "Calculating Simulations");
                        break;
                    case CBOSessionStatus.DoneCalculatingSimulation:
                        self.CalcProgressProvider.setProgressMessage("4", "Calculated Simulations");
                        break;
                    case CBOSessionStatus.WritingResultsToDatabase:
                        self.CalcProgressProvider.setProgressMessage("5", "Writing Results");
                        break;
                    case CBOSessionStatus.Done:
                        if (self.CalcProgressProvider.process_running) self.CalcProgressProvider.finish();
                        break;
                }

                // Update Message Provider
                self.CalcMessageProvider.clearMessages();
                self._calcMessageProvider.addTextMessages("Warn", MessageType.Warning, response.sWarnings);
                self._calcMessageProvider.addTextMessages("Err", MessageType.Error, response.sErrors);
                return response;
            }
        } catch (err) {
            self.CalcProgressProvider.exception(err);
        } finally {
        }
        return bGoFast;
    }

    get CBO(): ConstraintBasedOptimizerBO {
        return this._CBOBO;
    }

    get CalcProgressProvider(): IProgressProvider {
        return this._calcProgressProvider;
    }

    get CalcMessageProvider(): IMessageProvider {
        return this._calcMessageProvider;
    }

    //-- Changed to async behavior
    async Calculate(opts: ICBOCalculatorCalculateOptions): Promise<void> {
        const self = this;

        // 1. Update Prices and Save (Validate)
        // 2. Run CBO Evaluator

        const aBO: ConstraintBasedOptimizerBO = self._CBOBO;

        if (_.isEmpty(aBO)) return;

        // 1. Update Prices and Save (Validate)
        try {
            const aCBOService = new CBOService();

          //  let bo = await aBO.Save();
            if (!aBO.bIsSaved) return;

            // 2. Run CBO Evaluator
            //self._detailStatus.CalculationStep = CBOSessionStatus.CalculationInitiated;
            const results = await aCBOService.CalculateCBO(aBO.WB_CBO.cif.WB_CBO_ID, opts);
            if (results.bSuccess) {
                await aBO.ReloadMetrics();
                aBO.drWB_CBO.cif.LAST_CALC_DTTS = DateUtils.convertToSAPBWTs(new Date());
                await aBO.dbContext.Save(esWB_CBO, aBO.drWB_CBO);
            }
          //  let response = await self.UpdateStatus();
          //  return response;
            return
        } catch (err) {
            throw err;
        }
    }

    get OnCalculateComplete(): BehaviorSubject<CBOCalculator> {
        return this._onCalculationComplete;
    }

    get DetailedProgress(): ICBOCalculatorDetailProgress {
        return this._detailStatus;
    }

    get OnDetailedProgressChanged(): BehaviorSubject<ICBOCalculator> {
        return this._onDetailedProgressChanged;
    }
}
